import React, { useState } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { assistantService } from '../../api/services';
import { AssistantDevis as AssistantDevisData } from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import toast from 'react-hot-toast';

/**
 * Assistant commercial (TF-026) : saisir une demande client en langage naturel et
 * obtenir un brouillon de devis (articles + quantités) à vérifier avant envoi.
 */
export function AssistantDevis() {
  const [demande, setDemande] = useState('');
  const [result, setResult] = useState<AssistantDevisData | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!demande.trim()) { toast.error('Décrivez la demande du client'); return; }
    setLoading(true);
    try { setResult(await assistantService.suggererDevis(demande)); }
    catch { toast.error('Suggestion indisponible'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Assistant devis</h1>
        <p className="page-subtitle">Décrivez le besoin du client — l'assistant propose un brouillon de devis</p>
      </div>

      <div className="card space-y-3">
        <textarea
          className="input-field h-28"
          placeholder="Ex. : Le client souhaite équiper 3 postes de travail avec ordinateurs et souris."
          value={demande}
          onChange={(e) => setDemande(e.target.value)}
        />
        <button onClick={run} className="btn-primary" disabled={loading}>
          <SparklesIcon className="h-4 w-4 mr-1" />
          {loading ? 'Analyse…' : 'Proposer un devis'}
        </button>
      </div>

      {result && (
        <div className="card">
          <p className="text-sm text-gray-600 mb-3">
            {result.commentaire}
            {!result.genereParIa && <span className="text-xs text-gray-400"> (mode hors-ligne)</span>}
          </p>
          {result.lignes.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">Aucune ligne proposée.</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50"><tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-4 py-2">Article</th>
                <th className="px-4 py-2 text-right">Quantité</th>
                <th className="px-4 py-2 text-right">P.U. HT</th>
                <th className="px-4 py-2 text-right">Total HT</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {result.lignes.map((l, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2">{l.designation}</td>
                    <td className="px-4 py-2 text-right">{l.quantite}</td>
                    <td className="px-4 py-2 text-right"><MoneyDisplay amount={l.prixUnitaireHT} size="sm" /></td>
                    <td className="px-4 py-2 text-right font-medium">
                      <MoneyDisplay amount={l.prixUnitaireHT * l.quantite} size="sm" />
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td className="px-4 py-2" colSpan={3}>Total HT estimé</td>
                  <td className="px-4 py-2 text-right"><MoneyDisplay amount={result.totalHTEstime} size="sm" /></td>
                </tr>
              </tbody>
            </table>
          )}
          <p className="text-xs text-gray-400 mt-3">Brouillon indicatif — créez le devis dans l'écran Devis pour l'officialiser.</p>
        </div>
      )}
    </div>
  );
}

export default AssistantDevis;
